import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import React from 'react';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import { cloneDeep, isNil, isEmpty, get, filter, map, isEqual, orderBy, keys } from 'lodash';
import { Row, Col, Button, Alert } from 'react-bootstrap';

import { TaskSchemaOut } from '../../../api/Tasks/TaskSchema';
import * as taskOps from '../../../api/Tasks/task-operations';
import TasksCollection from '../../../api/Tasks/Tasks';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';
import TASK_CONFIG from '../../../modules/task-graph-configuration';
import {
  subtaskParametersFormWorkflow,
  connectionParametersFormWorkflow
} from '../../../modules/tasks-utilities';

import PageHeader from '../../atoms/PageHeader';
import TasksDropdown from '../../atoms/TasksDropdown';
import DebugTasksUtility from '../../components/DebugTasksUtility';
import Loading from '../../components/Loading';
import NodeParams from '../../components/NodeParams';
import TaskerGraph from '../../components/TaskerGraph';
import TableList from '../../components/TableList';

const { isBeta } = Meteor.settings.public;

const BUTTON_HEIGHT = 38;
const HEADER_HEIGHT = 51; // When it is all in the same line
const NAV_BAR_HEIGHT = 47;
const PER_PAGE = 15;
const SAVED_GROUP = 'Saved Tasks';
const SM_WIDTH = 576;
const XMARGIN = 30;

class Tasker extends React.Component {
  debugComponent = React.createRef();

  state = {
    currentPage: 1,
    hasError: false,
    loading: true,
    myConfigState: TASK_CONFIG,
    selectedGroup: 'No Group Selected',
    selectedNodeId: null,
    selectedTask: {},
    sort: { name: 1 },
    tasks: []
  };

  componentDidMount() {
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);
    this.loadTasklist();
  }

  componentDidUpdate(prevProps, prevState) {
    const { savedTaskList } = this.props;
    const { loading, selectedGroup, selectedNodeId, selectedTask } = this.state;
    const { savedTaskList: prevSavedTaskList } = prevProps;
    const {
      loading: prevLoading,
      selectedGroup: prevSelectedGroup,
      selectedNodeId: prevSelectedNodeId,
      selectedTask: prevSelectedTask
    } = prevState;

    if (!isEqual(loading, prevLoading) || !isEqual(savedTaskList, prevSavedTaskList))
      this.addSavedTasks(savedTaskList);

    if (
      !isEqual(selectedGroup, prevSelectedGroup) ||
      !isEqual(selectedNodeId, prevSelectedNodeId) ||
      !isEqual(selectedTask, prevSelectedTask)
    )
      this.updateWindowDimensions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  addSavedTasks = savedTaskList => {
    const { loading, tasks } = this.state;
    if (loading) return;
    const validatedTasks = filter(tasks, task => task.group !== SAVED_GROUP);
    const updatedSavedTasks = map(savedTaskList, savedTask => {
      const validatedSavedTask = cloneDeep(savedTask);
      validatedSavedTask.nodes = map(validatedSavedTask.nodes, node => {
        const validatedNode = cloneDeep(node);
        validatedNode.isNodeReady = true;
        return validatedNode;
      });
      delete validatedSavedTask._id;
      validatedSavedTask.isTaskReadyToSend = taskOps.isTaskReady(validatedSavedTask);
      return validatedSavedTask;
    });
    this.setState({ tasks: [...updatedSavedTasks, ...validatedTasks] });
  };

  changePage = pageNumber => this.setState({ currentPage: pageNumber });

  closeForm = () => this.setState({ selectedNodeId: null });

  handleSort = value =>
    this.setState(prevState => ({ sort: { [value]: prevState.sort[value] * -1 } }));

  loadTasklist = async () => {
    try {
      const debugIP = isBeta ? get(this, 'debugComponent.current.serverIP', '') : '';
      const validatedTasks = await methodPromise('tasks.getTasklist', debugIP);
      this.setState({ tasks: [...validatedTasks], loading: false, hasError: false });
    } catch (error) {
      console.error(error);
      this.setState({ hasError: true });
      handleErrorInClient(error);
    }
  };

  runTask = async () => {
    const { selectedTask } = this.state;
    const taskToSend = cloneDeep(selectedTask);
    const cleanedTask = TaskSchemaOut.clean(taskToSend);
    try {
      let debugIP = '';
      if (isBeta) {
        debugIP = get(this, 'debugComponent.current.serverIP', '');
      }
      await methodPromise('tasks.runTask', { debugIP, taskToRun: cleanedTask });
      this.redirectToTaskMonitor();
    } catch (error) {
      console.error(error);
      this.setState({ hasError: true });
      handleErrorInClient(error);
    }
  };

  saveConnectionParams = (form, selectedSourceId) => {
    const { selectedTask, selectedNodeId } = this.state;

    try {
      const newSelectedTask = connectionParametersFormWorkflow({
        form,
        selectedTask,
        selectedNodeId,
        selectedSourceId
      });
      this.setState({
        selectedTask: newSelectedTask,
        selectedNodeId: null
      });
      Bert.alert('Saved Values', 'success');
    } catch (error) {
      console.error(error);
      handleErrorInClient(error);
    }
    this.updateWindowDimensions();
  };

  saveSubtaskParams = form => {
    const { selectedTask, selectedNodeId } = this.state;
    try {
      const newSelectedTask = subtaskParametersFormWorkflow({
        form,
        selectedTask,
        selectedNodeId
      });
      this.setState({
        selectedTask: newSelectedTask,
        selectedNodeId: null
      });
      Bert.alert('Saved Values', 'success');
    } catch (error) {
      console.error(error);
      handleErrorInClient(error);
    }
    this.updateWindowDimensions();
  };

  selectGroup = newGroup =>
    this.setState({ selectedGroup: cloneDeep(newGroup), selectedTask: {}, selectedNodeId: null });

  selectNode = async newSelectedNodeId => this.setState({ selectedNodeId: newSelectedNodeId });

  selectTask = async newTask => this.setState({ selectedTask: newTask, selectedNodeId: null });

  updateWindowDimensions = () => {
    const { myConfigState } = this.state;

    const newSizeConfig = cloneDeep(myConfigState);
    if (window.innerWidth >= SM_WIDTH) newSizeConfig.width = window.innerWidth / 2 - XMARGIN;
    else newSizeConfig.width = window.innerWidth - XMARGIN;

    const verticalHeight = NAV_BAR_HEIGHT + HEADER_HEIGHT + BUTTON_HEIGHT;
    newSizeConfig.height = (window.innerHeight - verticalHeight) / 2;

    this.setState({ myConfigState: newSizeConfig });
  };

  redirectToTaskMonitor() {
    const { history } = this.props;
    Bert.alert('Task sent To Run', 'success');
    history.push('/taskmonitor');
  }

  render() {
    const { isPremium, loadingSavedTasks } = this.props;
    const {
      currentPage,
      hasError,
      loading,
      myConfigState,
      selectedGroup,
      selectedNodeId,
      selectedTask,
      sort,
      tasks
    } = this.state;

    const searchTasks = filter(tasks, task => task.group === selectedGroup);
    const sortedCategory = keys(sort)[0];
    const orderedTasks = orderBy(
      searchTasks,
      [sortedCategory],
      [sort[sortedCategory] === 1 ? 'asc' : 'desc']
    );
    const skip = currentPage * PER_PAGE - PER_PAGE;
    const selectedTasks = filter(
      orderedTasks,
      (task, index) => index >= skip && index < skip + PER_PAGE
    );

    const tableHeaders = [
      {
        name: `Tasks - ${selectedGroup}`,
        sortValue: 'name',
        totalCount: searchTasks.length
      }
    ];
    const tablePageHeaderProps = {
      hasImportButton: false,
      hasSearchInput: false,
      isPremium,
      newItems: []
    };
    const tableHeaderRowProps = { sort, tableHeaders, handleSort: this.handleSort };
    const tableContentProps = {
      clickAction: 'showTaskItem',
      datesColumns: false,
      deleteButton: false,
      isTableEmpty: isEmpty(selectedTasks),
      itemName: 'Tasks',
      items: selectedTasks,
      loading,
      numberOfColumns: 1,
      handleSelectRow: this.selectTask
    };
    const tablePaginationProps = {
      currentPage,
      itemsPerPage: PER_PAGE,
      totalCount: searchTasks.length,
      changePage: this.changePage
    };
    const { isTaskReadyToSend } = selectedTask;

    if (hasError)
      return (
        <>
          <DebugTasksUtility stateRef={this.debugComponent} loadTasklist={this.loadTasklist} />
          <Alert variant="danger">
            <Alert.Heading>Server Error</Alert.Heading>
            <p>Please Contact your tasks provider for help.</p>
          </Alert>
        </>
      );
    if (loading || loadingSavedTasks) return <Loading />;
    if (isEmpty(tasks))
      return (
        <>
          <DebugTasksUtility stateRef={this.debugComponent} loadTasklist={this.loadTasklist} />
          <Alert variant="danger">
            <Alert.Heading>No tasks found</Alert.Heading>
            <p>Please Contact your tasks provider for help.</p>
          </Alert>
        </>
      );
    return (
      <>
        <DebugTasksUtility stateRef={this.debugComponent} loadTasklist={this.loadTasklist} />
        <PageHeader>
          <h4>iTasker</h4>
          <TasksDropdown tasklist={tasks} selectGroup={this.selectGroup} />
        </PageHeader>
        <Row>
          <Col xs={12} sm={6}>
            {isNil(selectedNodeId) ? (
              <TableList
                childProps={{
                  tablePageHeaderProps,
                  tableHeaderRowProps,
                  tableContentProps,
                  tablePaginationProps
                }}
                isFreeUserAtLimit={false}
                isPaginationOn
              />
            ) : (
              <NodeParams
                key={selectedNodeId}
                myConfig={myConfigState}
                selectedNodeId={selectedNodeId}
                task={selectedTask}
                close={this.closeForm}
                saveConnectionParams={this.saveConnectionParams}
                saveSubtaskParams={this.saveSubtaskParams}
              />
            )}
          </Col>
          <Col xs={12} sm={6}>
            {taskOps.getLabel(selectedTask)}
            <TaskerGraph
              myConfig={myConfigState}
              task={selectedTask}
              selectNode={this.selectNode}
            />
            <Button variant="success" onClick={this.runTask} disabled={!isTaskReadyToSend}>
              Run Task
            </Button>
          </Col>
        </Row>
      </>
    );
  }
}

Tasker.defaultProps = {
  loadingSavedTasks: true,
  savedTaskList: []
};

Tasker.propTypes = {
  history: PropTypes.object.isRequired,
  isPremium: PropTypes.bool.isRequired,
  loadingSavedTasks: PropTypes.bool,
  savedTaskList: PropTypes.array
};

export default withTracker(() => {
  const tasksSub = Meteor.subscribe('tasks.reuseSavedTasks');
  const taskQuery = { deleted: { $eq: 'no' }, group: { $eq: SAVED_GROUP } };

  return {
    loadingSavedTasks: !tasksSub.ready(),
    savedTaskList: TasksCollection.find(taskQuery).fetch()
  };
})(Tasker);
